import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export type RightSidebarType = 'filters' | 'analysis' | 'both' | 'none';

interface LayoutContextType {
  // Left sidebar state
  isLeftSidebarExpanded: boolean;
  setIsLeftSidebarExpanded: (expanded: boolean) => void;
  
  // Right sidebars state
  rightSidebarType: RightSidebarType;
  setRightSidebarType: (type: RightSidebarType) => void;
  
  // Computed widths in pixels
  leftSidebarWidth: number;
  rightSidebarWidth: number;
  
  // Helper functions
  getMainContentStyle: () => React.CSSProperties;
  getContentAreaStyle: () => React.CSSProperties;
  getGridColumnsClass: () => string;
  shouldShowDualPlots: () => boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

// Constants for sidebar widths
const SIDEBAR_WIDTHS = {
  LEFT_EXPANDED: 256, // 64 * 4 = 256px (w-64 in tailwind)
  LEFT_COLLAPSED: 64, // 16 * 4 = 64px (w-16 in tailwind)
  RIGHT_SINGLE: 320, // Width for single right sidebar
  RIGHT_BOTH: 640, // Width for both right sidebars
  RIGHT_NONE: 0,
};

interface LayoutProviderProps {
  children: ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const [isLeftSidebarExpanded, setIsLeftSidebarExpanded] = useState(false);
  const [rightSidebarType, setRightSidebarType] = useState<RightSidebarType>('none');
  
  // Calculate widths
  const leftSidebarWidth = isLeftSidebarExpanded 
    ? SIDEBAR_WIDTHS.LEFT_EXPANDED 
    : SIDEBAR_WIDTHS.LEFT_COLLAPSED;
    
  const rightSidebarWidth = rightSidebarType === 'none' 
    ? SIDEBAR_WIDTHS.RIGHT_NONE
    : rightSidebarType === 'both'
    ? SIDEBAR_WIDTHS.RIGHT_BOTH
    : SIDEBAR_WIDTHS.RIGHT_SINGLE;
  
  // Get dynamic styles for main content
  const getMainContentStyle = useCallback((): React.CSSProperties => {
    return {
      marginLeft: `${leftSidebarWidth}px`,
      width: `calc(100% - ${leftSidebarWidth}px)`,
      transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out',
    };
  }, [leftSidebarWidth]);
  
  // Get dynamic styles for content area (cards, table, etc.)
  const getContentAreaStyle = useCallback((): React.CSSProperties => {
    // Adjust padding based on right sidebar state
    const paddingX = rightSidebarType === 'none' ? '2rem' : rightSidebarType === 'both' ? '1rem' : '1.5rem';
    
    return {
      paddingLeft: paddingX,
      paddingRight: paddingX,
      paddingTop: '1.5rem',
      paddingBottom: '1.5rem',
      maxWidth: '100%',
      transition: 'padding 0.3s ease-in-out',
    };
  }, [rightSidebarType]);
  
  // Get whether to show dual plots side by side
  const shouldShowDualPlots = useCallback((): boolean => {
    // Show dual plots when:
    // 1. No right sidebars (maximum space)
    // 2. Viewport is wide enough
    // 3. Not on mobile
    if (typeof window !== 'undefined') {
      return (
        rightSidebarType === 'none' && 
        window.innerWidth >= 1600 &&
        !('ontouchstart' in window) // Not a touch device
      );
    }
    return false;
  }, [rightSidebarType]);
  
  // Get dynamic grid columns class for cards
  const getGridColumnsClass = useCallback((): string => {
    if (rightSidebarType === 'none') {
      // No right sidebars - use more columns
      return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5';
    } else if (rightSidebarType === 'both') {
      // Both sidebars open - use fewer columns
      return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    } else {
      // One sidebar open - medium number of columns
      return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    }
  }, [rightSidebarType]);
  
  const value: LayoutContextType = {
    isLeftSidebarExpanded,
    setIsLeftSidebarExpanded,
    rightSidebarType,
    setRightSidebarType,
    leftSidebarWidth,
    rightSidebarWidth,
    getMainContentStyle,
    getContentAreaStyle,
    getGridColumnsClass,
    shouldShowDualPlots,
  };
  
  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
